// Хуки для управления группами доступа и RLS-привязками
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { message } from 'antd'
import * as accessGroupsApi from '@/api/accessGroups'

// ---------------------------------------------------------------------------
// Groups
// ---------------------------------------------------------------------------

export const useAccessGroups = (orgId?: string) =>
  useQuery({
    queryKey: ['access-groups', orgId],
    queryFn: () => accessGroupsApi.fetchGroups(orgId),
  })

export const useAccessGroup = (groupId: string) =>
  useQuery({
    queryKey: ['access-groups', groupId],
    queryFn: () => accessGroupsApi.fetchGroup(groupId),
    enabled: !!groupId,
  })

export const useCreateGroup = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: accessGroupsApi.createGroup,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['access-groups'] })
      message.success('Группа создана')
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.detail || 'Ошибка создания группы')
    },
  })
}

export const useUpdateGroup = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ groupId, data }: { groupId: string; data: accessGroupsApi.UpdateGroupRequest }) =>
      accessGroupsApi.updateGroup(groupId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['access-groups'] })
      message.success('Группа обновлена')
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.detail || 'Ошибка обновления группы')
    },
  })
}

export const useDeactivateGroup = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: accessGroupsApi.deactivateGroup,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['access-groups'] })
      message.success('Группа деактивирована')
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.detail || 'Ошибка деактивации')
    },
  })
}

// ---------------------------------------------------------------------------
// Members
// ---------------------------------------------------------------------------

export const useAddMember = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ groupId, userId }: { groupId: string; userId: string }) =>
      accessGroupsApi.addMember(groupId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['access-groups'] })
      message.success('Участник добавлен')
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.detail || 'Ошибка добавления участника')
    },
  })
}

export const useRemoveMember = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ groupId, userId }: { groupId: string; userId: string }) =>
      accessGroupsApi.removeMember(groupId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['access-groups'] })
      message.success('Участник удалён')
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.detail || 'Ошибка удаления участника')
    },
  })
}

// ---------------------------------------------------------------------------
// RLS Bindings
// ---------------------------------------------------------------------------

export const useUserRlsBindings = (userId: string, enabled = true) =>
  useQuery({
    queryKey: ['rls-bindings', userId],
    queryFn: () => accessGroupsApi.fetchUserRlsBindings(userId),
    enabled: !!userId && enabled,
  })

export const useBulkReplaceRls = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ userId, bindings }: { userId: string; bindings: accessGroupsApi.BulkRlsRequest }) =>
      accessGroupsApi.bulkReplaceRls(userId, bindings),
    onSuccess: (_, { userId }) => {
      queryClient.invalidateQueries({ queryKey: ['rls-bindings', userId] })
      message.success('RLS-привязки обновлены')
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.detail || 'Ошибка обновления RLS')
    },
  })
}

export const useDeleteRlsBinding = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (bindingId: string) => accessGroupsApi.deleteRlsBinding(bindingId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rls-bindings'] })
      message.success('RLS-привязка удалена')
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.detail || 'Ошибка удаления RLS-привязки')
    },
  })
}

export const useEffectiveRlsRows = (userId: string, enabled = true) =>
  useQuery({
    queryKey: ['effective-rls', userId],
    queryFn: () => accessGroupsApi.fetchEffectiveRlsRows(userId),
    enabled: !!userId && enabled,
  })

export const usePreviewEffectiveRls = () => {
  return useMutation({
    mutationFn: ({ userId, bindings }: { userId: string; bindings: accessGroupsApi.BulkRlsRequest }) =>
      accessGroupsApi.previewEffectiveRlsRows(userId, bindings),
    onError: (error: any) => {
      message.error(error?.response?.data?.detail || 'Ошибка предпросмотра effective RLS')
    },
  })
}

// ---------------------------------------------------------------------------
// Entries (DataLens entries bound to access groups)
// ---------------------------------------------------------------------------

export const useGroupEntries = (groupId: string) =>
  useQuery({
    queryKey: ['access-group-entries', groupId],
    queryFn: () => accessGroupsApi.fetchEntries(groupId),
    enabled: !!groupId,
  })

export const useAddEntry = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ groupId, data }: { groupId: string; data: accessGroupsApi.AddEntryRequest }) =>
      accessGroupsApi.addEntry(groupId, data),
    onSuccess: (_, { groupId }) => {
      queryClient.invalidateQueries({ queryKey: ['access-group-entries', groupId] })
      message.success('Дашборд добавлен')
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.detail || 'Ошибка добавления дашборда')
    },
  })
}

export const useDeleteEntry = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ groupId, entryId }: { groupId: string; entryId: string }) =>
      accessGroupsApi.deleteEntry(groupId, entryId),
    onSuccess: (_, { groupId }) => {
      queryClient.invalidateQueries({ queryKey: ['access-group-entries', groupId] })
      message.success('Дашборд удалён')
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.detail || 'Ошибка удаления дашборда')
    },
  })
}

// ---------------------------------------------------------------------------
// Per-group sync status
// ---------------------------------------------------------------------------

export const useGroupSyncStatus = (groupId: string) =>
  useQuery({
    queryKey: ['group-sync-status', groupId],
    queryFn: () => accessGroupsApi.fetchGroupSyncStatus(groupId),
    enabled: !!groupId,
  })

export const useSyncGroupEntries = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (groupId: string) => accessGroupsApi.syncGroupEntries(groupId),
    onSuccess: (data, groupId) => {
      queryClient.invalidateQueries({ queryKey: ['access-group-entries', groupId] })
      queryClient.invalidateQueries({ queryKey: ['group-sync-status', groupId] })
      if (data.resolved_count > 0) {
        message.success(`Синхронизировано: ${data.resolved_count} дашбордов`)
      } else {
        message.info('Нет новых дашбордов для синхронизации')
      }
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.detail || 'Ошибка синхронизации')
    },
  })
}

// ---------------------------------------------------------------------------
// RLS Options (autocomplete from ClickHouse)
// ---------------------------------------------------------------------------

export const useRlsOptions = (params: accessGroupsApi.RlsOptionsParams | null) =>
  useQuery({
    queryKey: ['rls-options', params],
    queryFn: () => accessGroupsApi.fetchRlsOptions(params!),
    enabled: !!params,
    staleTime: 5 * 60 * 1000, // cache 5 min — reference data changes rarely
  })

// ---------------------------------------------------------------------------
// Access Config (deploy, diff, status)
// ---------------------------------------------------------------------------

export const useAccessConfigDiff = (enabled = false) =>
  useQuery({
    queryKey: ['access-config-diff'],
    queryFn: accessGroupsApi.fetchAccessConfigDiff,
    enabled,
  })

export const useAccessConfigStatus = () =>
  useQuery({
    queryKey: ['access-config-status'],
    queryFn: accessGroupsApi.fetchAccessConfigStatus,
  })

export const useDeployAccessConfig = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: accessGroupsApi.deployAccessConfig,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['access-config-status'] })
      queryClient.invalidateQueries({ queryKey: ['access-config-diff'] })
      message.success('Права доступа синхронизированы')
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.detail || 'Ошибка синхронизации прав доступа')
    },
  })
}
